/**
 * Auth-identity derivation for the SDKProvider identity-flip effect
 * (Slice 3 of the auth wiring; the HIGH-severity cache-leak fix).
 *
 * The SDK is *app-owns-auth*: the consumer passes a `JwtResponse` as the
 * `jwt` prop and swaps it on login / logout / account-switch. The
 * provider must detect a genuine IDENTITY change (so it can drop the
 * previous identity's user-scoped cache) WITHOUT false-positiving on a
 * mere token refresh (same user, new `jwt` string + `expires_at`).
 *
 * Identity key = the normalised `allow_sub` scope, because:
 *   - It is the literal server-enforced authorisation boundary (Synadia
 *     rejects any SUB outside it) — i.e. it *is* the identity boundary.
 *   - Anon → the generated public roots (same for every anon caller).
 *   - Authed → those roots + generated per-user leaf subjects that
 *     encode the user's pubkey, so user-A ≠ user-B.
 *   - It is STABLE across mid-session token refreshes: a refresh rotates
 *     `jwt` + `expires_at` but the authorised roots are unchanged → no
 *     false flip (which would needlessly wipe the cache every refresh).
 *
 * Decode-free + platform-neutral by construction (array sort+join only)
 * — no JWS/base64/atob/Buffer assumptions, so it is safe in `src/`
 * core (web + RN) per `.claude/rules/platform-agnostic.md`.
 */

import type { JwtResponse, MintKind } from './auth.js';

/** Stable sentinel for every anonymous / no-JWT caller. */
export const ANON_IDENTITY = 'anon' as const;

/**
 * Derive a stable identity key from the live `jwt` prop. Equal keys ⇒
 * same identity (no cache flip); different keys ⇒ a real
 * login/logout/account-switch ⇒ the provider tears down + clears the
 * auth-keyed cache before reconnecting under the new identity.
 */
export function deriveAuthIdentity(jwt: JwtResponse | null): string {
  if (jwt === null) return ANON_IDENTITY;
  const token = jwt.jwt;
  if (token === '' || token === 'anon') return ANON_IDENTITY;
  const roots = jwt.allow_sub;
  if (!Array.isArray(roots) || roots.length === 0) return ANON_IDENTITY;
  // Sort a copy so ordering differences in the minted list don't read
  // as an identity change; join on a char that cannot appear in a NATS
  // subject token.
  return `scope:${[...roots].sort().join('|')}`;
}

/** Cache strategy for a confirmed identity flip (`prev !== next`). */
export type IdentityFlipStrategy = 'soft-login' | 'hard-purge';

/**
 * Decide how the identity-flip effect must treat the query cache.
 *
 * - `'soft-login'` — anon → user. The pre-login cache holds ONLY
 *   anon-readable rows (the caller was unauthenticated), so there is no
 *   private data to purge. Wiping it would needlessly tear down every
 *   cached public surface — most visibly the markets hero dropping to a
 *   skeleton and back. The effect keeps the cache, reconnects under the
 *   new identity, then invalidates so viewer-scoped fields refetch in
 *   place: data stays visible, no `pending` flash.
 * - `'hard-purge'` — logout (user → anon) AND account-switch (user-A →
 *   user-B). Both leave a REAL user's private data in the cache, so it
 *   MUST be cleared (in-memory + persisted) before reconnecting. This is
 *   the original HIGH-severity cache-leak guard.
 *
 * Callers only invoke this on a genuine flip; `prev === next` never
 * reaches here.
 */
export function planIdentityFlip(prev: string, next: string): IdentityFlipStrategy {
  return prev === ANON_IDENTITY && next !== ANON_IDENTITY ? 'soft-login' : 'hard-purge';
}

/**
 * Session restore starts with an anonymous UI identity because the auth tree
 * hydrates inside SDKProvider. The cookie-bound NATS mint may already return a
 * live user credential before that outer identity label catches up. Reuse is
 * safe only for that exact anon→user startup transition; genuine login,
 * logout, account-switch, and broken-socket paths still replace credentials.
 */
export function shouldReuseConnectedCredential(
  prevIdentity: string,
  nextIdentity: string,
  credentialKind: MintKind | null,
  hasLiveConnection: boolean,
): boolean {
  return (
    prevIdentity === ANON_IDENTITY &&
    nextIdentity !== ANON_IDENTITY &&
    credentialKind === 'user' &&
    hasLiveConnection
  );
}
